import React, { useGlobal, useState } from "reactn"
import { graphql, StaticQuery } from "gatsby"
import styled from "styled-components"
import { FluidObject } from "gatsby-image"
import convert from "../utils/convert"
import { Apartment, ApartmentProps } from "./Apartments"
import Image, { ImageProps } from "./Image"
import Slideshow from "./Slideshow"
import Dialog from "./Dialog"
import Wrapper from "./Wrapper"

const Svg = styled.svg`
  background-size: contain;
  * {
    fill: transparent;
    cursor: pointer;
    transition: fill 0.2s;
    stroke: #fff;
    stroke-width: 2px;
  }
`

const A = styled.a`
  &:focus,
  &:hover {
    outline: none;
    * {
      cursor: pointer;
      fill: rgba(19, 199, 58, 0.35);
    }
  }
`

const SvgContainer = styled.div`
  @media(max-width: 1000px) {
    width: 150vw;
    transform: translateX(-25vw);
  }
  @media(max-width: 600px) {
    width: 200vw;
    transform: translateX(-50vw);
  }
`

interface FloorApartmentProps {
  id: string
  frontmatter: ApartmentProps
}

interface FloorQueryProps {
  node: {
    id: string
    frontmatter: {
      title: string
      text: string
      gallery: ImageProps[]
      pickerOverlay: string
    }
    fields: {
      apartments: FloorApartmentProps[]
    }
  }
}

interface FloorPickerProps {
  viewBox: string
  backgroundImage: {
    childImageSharp: {
      fluid: FluidObject
    }
  }
  data: {
    floors: { edges: FloorQueryProps[] }
  }
}

const FloorPicker: React.FC<FloorPickerProps> = ({
  viewBox,
  backgroundImage,
  data
}) => {
  const { edges: floors } = data.floors
  const { 1: setActiveApartmentId } = useGlobal<any>("activeApartmentId")
  const [ activeFloorId, setActiveFloorId ] = useState<string>()
  const activeFloor = floors[
    floors.findIndex(({ node }) => node.id === activeFloorId)
  ] || null

  return (
    <div className="overflow-x-hidden">
      <SvgContainer>
        <Svg
          xmlns="http://www.w3.org/2000/svg"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          viewBox={viewBox}
          className="w-full"
          style={{
            backgroundImage: `url('${backgroundImage.childImageSharp.fluid.src}')`
          }}
        >
          {floors.map(({ node: floor }: FloorQueryProps) => (
            <g key={floor.id}>
              <A
                id={`picker-selector__select-unit__unit-id--${floor.id}__link`}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setActiveFloorId(floor.id)
                }}
              >
                <g
                  dangerouslySetInnerHTML={{
                    __html: convert(floor.frontmatter.pickerOverlay)
                  }}
                />
              </A>
            </g>
          ))}
        </Svg>
      </SvgContainer>

      {activeFloorId && (
        <FloorDialog
          shown={!!activeFloorId}
          title={activeFloor.node.frontmatter.title}
          text={activeFloor.node.frontmatter.text}
          gallery={activeFloor.node.frontmatter.gallery}
          apartments={activeFloor.node.fields.apartments}
          setActiveFloorId={setActiveFloorId}
          setActiveApartmentId={setActiveApartmentId}
        />
      )}
    </div>
  )
}

interface FloorDialogProps {
  shown: boolean
  title: string
  text: string
  gallery: ImageProps[]
  apartments: FloorApartmentProps[]
  setActiveFloorId: Function
  setActiveApartmentId: Function
}

const FloorDialog: React.FC<FloorDialogProps> = ({
  shown,
  title,
  text,
  gallery,
  apartments,
  setActiveFloorId,
  setActiveApartmentId
}) => (
  <Dialog
    title={title}
    shown={shown}
    onClose={() => setActiveFloorId(null)}
    toBody={true}
  >
    <div className="flex items-center">
      {gallery.map((image: ImageProps, index: number) => (
        <figure
          key={index}
          className={`mx-auto flex-1 ${index === 0 ? 'flex-2' : ''}`}
          style={{ maxWidth: 650 }}
        >
          <Image
            src={image.src}
            alt={image.alt}
          />
        </figure>
      ))}
    </div>

    <div className="p-4">
      <Wrapper
        size="sm"
        className="mx-auto text-xs text-center px-12 md:px-0"
      >
        <div
          dangerouslySetInnerHTML={{
            __html: convert(text)
          }}
        />
      </Wrapper>
    </div>

    <div
      className="py-3 flex items-center justify-center flex-col text-white relative"
      style={{ backgroundColor: "#0e101c" }}
    >
      <Slideshow
        className="px-6"
        options={{
          slidesPerView: 3,
          centerInsufficientSlides: true,
          spaceBetween: 30,
          roundLengths: true,
          breakpoints: {
            768: {
              slidesPerView: 2
            },
            500: {
              slidesPerView: 1
            }
          }
        }}
      >
        {apartments.map(({ frontmatter: apartment, id }) => (
          <div key={id}>
            <Apartment
              id={id}
              title={apartment.title}
              subtitle={apartment.subtitle}
              sold={apartment.sold}
              price={apartment.price}
              bidUrl={apartment.bidUrl}
              text={apartment.text}
              colors={apartment.colors}
              rom={apartment.rom}
              areal={apartment.areal}
              setActiveApartmentId={setActiveApartmentId}
              className="border-t-2 border-solid mt-3 pt-3"
              style={{
                borderColor: apartment.floorLegend
              }}
            />
          </div>
        ))}
      </Slideshow>
    </div>
  </Dialog>
)

const floorQuery = graphql`
  query FloorsQuery {
    floors: allMarkdownRemark(
      filter: {
        frontmatter: {
          templateKey: { eq: "floor" },
          shown: { eq: true }
        }
      }
    ) {
      edges {
        node {
          fields {
            apartments {
              id
              frontmatter {
                templateKey
                shown
                floorTitle
                floorLegend
                order
                title
                subtitle
                sold
                price
                bidUrl
                text
                colors
                rom {
                  baderom
                  soverom
                  entre
                }
                areal {
                  inside
                  primaryRoom
                  outside
                }
                gallery {
                  src {
                    childImageSharp {
                      fluid(maxHeight: 500, maxWidth: 600, quality: 100) {
                        ...GatsbyImageSharpFluid
                      }
                    }
                    publicURL
                  }
                  alt
                }
              }
            }
          }
          id
          frontmatter {
            shown
            title
            text
            gallery {
              src {
                childImageSharp {
                  fluid(maxWidth: 1920 quality: 100) {
                    ...GatsbyImageSharpFluid
                  }
                }
              }
              alt
            }
            pickerOverlay
          }
        }
      }
    }
  }
`

interface FloorPickerWrapperProps {
  viewBox: string
  backgroundImage: {
    childImageSharp: {
      fluid: FluidObject
    }
  }
}

export default (props: FloorPickerWrapperProps) => (
  <StaticQuery
    query={floorQuery}
    render={(data) => <FloorPicker
      data={data}
      {...props}
    />}
  />
)
